import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getCacheGroupOperationalMetrics;

const DBO_CACHE_OPERATIONAL_METRICS_QUERY_KEY = 'dbo-cache-operational-metrics';

export type CacheGroupOperationalMetricsQueryData = {
  items: ApiResponse<typeof apiFn>['items'];
  summary: ApiResponse<typeof apiFn>['summary'];
  range: TimeRange;
  stepSeconds: number;
};

type CacheGroupOperationalMetricsQueryArgs<SELECT> = QueryOptions<
  CacheGroupOperationalMetricsQueryData,
  SELECT
> & {
  key: string;
  cacheGroupId?: string;
  cacheConfigId?: string;
  endpointName?: string;
  username?: string;
  stepSeconds?: number;
  getRange: () => TimeRange;
};

const getCacheGroupOperationalMetricsQueryKey = (
  params: {
    key?: string;
    cacheGroupId?: string;
    cacheConfigId?: string;
    endpointName?: string;
    username?: string;
    stepSeconds?: number;
  } = {}
) =>
  [
    DBO_CACHE_OPERATIONAL_METRICS_QUERY_KEY,
    params.key,
    params.cacheGroupId,
    params.cacheConfigId,
    params.endpointName,
    params.username,
    params.stepSeconds,
  ] as const;

const getCacheGroupOperationalMetricsQueryOptions = ({
  key,
  cacheGroupId,
  cacheConfigId,
  endpointName,
  username,
  stepSeconds = 60,
  getRange,
  enabled,
}: Omit<CacheGroupOperationalMetricsQueryArgs<unknown>, 'select'>) =>
  queryOptions({
    queryKey: getCacheGroupOperationalMetricsQueryKey({
      key,
      cacheGroupId,
      cacheConfigId,
      endpointName,
      username,
      stepSeconds,
    }),
    queryFn: async () => {
      const range = getRange();
      const { data } = await apiFn({
        id: cacheGroupId!,
        stepSeconds: stepSeconds,
        metricsRangeStart: range.from,
        metricsRangeEnd: range.to,
        configurationId: cacheConfigId,
        endpointName,
        username,
      });
      return { items: data.items, summary: data.summary, range, stepSeconds };
    },
    enabled: makeIsEnabled(!!cacheGroupId, enabled),
  });

export const useCacheGroupOperationalMetricsQuery = <
  SELECT = CacheGroupOperationalMetricsQueryData
>({
  key,
  cacheGroupId,
  cacheConfigId,
  endpointName,
  username,
  stepSeconds = 60,
  getRange,
  enabled,
  select,
  refetchInterval,
}: CacheGroupOperationalMetricsQueryArgs<SELECT>) => {
  return useQuery({
    ...getCacheGroupOperationalMetricsQueryOptions({
      key,
      cacheGroupId,
      cacheConfigId,
      endpointName,
      username,
      stepSeconds,
      getRange,
      enabled,
    }),
    select,
    refetchInterval,
  });
};
